import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Td,
  Tr,
  Box,
  chakra,
  useToast,
  Text,
  Table,
  Tbody,
  Flex,
  Spacer,
  Button,
  Grid,
  FormLabel,
  Input,
  IconButton,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { Fragment, useEffect, useState } from "react";
import { useProfile } from "@/contexts/ProfileContext";
import { AP_IB_EXAM_OPTIONS } from "@/utils/profile";
import { createSelectOptions } from "@/utils/ui/select-helper";
import { useEditProfileContext } from "./edit/shared";

const examOptions = createSelectOptions(AP_IB_EXAM_OPTIONS);

const APScore = ({ name, score }) => {
  const toast = useToast();
  const colors = [
    "black.500", // Score of 1
    "red.500", // Score of 2
    "orange.500", // Score of 3
    "yellow.500", // Score of 4
    "green.500", // Score of 5 and above
  ];

  const onClickI = () => {
    toast({
      title: `${name} Score`,
      description:
        "This score was not provided to colleges at the time of application—the student took the class their senior year.",
      status: "info",
      duration: 4000,
      isClosable: true,
    });
  };

  return (
    <Tr>
      <Td
        border="1px solid black"
        fontSize="3xl"
        fontWeight="bold"
        color={colors[Math.min(score ?? 1, colors.length) - 1]}
      >
        {!score ? (
          <chakra.i cursor="pointer" onClick={onClickI}>
            i
          </chakra.i>
        ) : (
          score
        )}
      </Td>
      <Td border="1px solid black" fontSize="xl">
        {name}
      </Td>
    </Tr>
  );
};

const APScores = () => {
  const { profile } = useProfile();

  return (
    <>
      <Box bg="whiteAlpha.700" maxHeight="30rem" overflowY="auto">
        <Table>
          <Tbody>
            {profile.ap_scores
              .sort((a, b) => {
                if (a.score === b.score) {
                  return 0;
                } else if (!a.score) {
                  return 1;
                } else if (!b.score) {
                  return -1;
                } else {
                  return b.score - a.score;
                }
              })
              .map(({ name, score }, i) => (
                <APScore name={name} score={score} key={i} />
              ))}
          </Tbody>
        </Table>
      </Box>
      <Text py={1}>(You may need to scroll to see all scores.)</Text>
    </>
  );
};

export const EditableAPScoresList = () => {
  const { initialProfileData, update } = useEditProfileContext();
  const [scores, setScores] = useState(initialProfileData.ap_scores);

  useEffect(() => update("ap_scores", scores), [scores]);

  return (
    <Box mt={4}>
      <Flex>
        <Spacer />
        <Button
          colorScheme="green"
          leftIcon={<AddIcon />}
          my={4}
          onClick={() => {
            setScores([...scores, { name: "", score: "" }]);
          }}
        >
          Add Exam Score
        </Button>
      </Flex>

      {scores.length > 0 && (
        <Grid templateColumns="min-content 10fr auto" rowGap={1} columnGap={4}>
          <FormLabel fontSize="xl">Score</FormLabel>

          <FormLabel fontSize="xl">Course Name</FormLabel>

          <Box />

          {scores.map(({ name, score }, i) => {
            const onUpdateField = (fieldName, value) => {
              const newScores = [...scores];
              newScores[i][fieldName] = value;
              setScores(newScores);
            };

            const onDelete = () => {
              const newScores = [...scores];
              newScores.splice(i, 1);
              setScores(newScores);
            };

            return (
              <Fragment key={i}>
                <Input
                  value={score}
                  placeholder="Pending"
                  variant="flushed"
                  type="number"
                  onChange={(e) => onUpdateField("score", e.target.value)}
                />

                <Select
                  value={{ label: name, value: name }}
                  options={examOptions}
                  placeholder="Exam Name"
                  variant="flushed"
                  onChange={(e) => onUpdateField("name", e.value)}
                />

                <IconButton
                  colorScheme="red"
                  variant="ghost"
                  icon={<DeleteIcon />}
                  onClick={onDelete}
                />
              </Fragment>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default APScores;
